import React from "react"

import Icon from "@/components/Icon"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { formatDate } from "@/components/ui/formatDate"
import type { UpcomingShift } from "@/hooks/useActiveEngagements"

interface SheriffTypeChangeWarningDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  personName: string
  upcomingShifts: UpcomingShift[]
  onConfirm: () => void
  isConfirming?: boolean
  trigger?: "sheriff-edit" | "application-complete"
}

const SheriffTypeChangeWarningDialog: React.FC<SheriffTypeChangeWarningDialogProps> = ({
  open,
  onOpenChange,
  personName,
  upcomingShifts,
  onConfirm,
  isConfirming = false,
  trigger = "sheriff-edit",
}) => {
  const shiftCount = upcomingShifts.length
  const isCompletion = trigger === "application-complete"

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Icon name="warningCircleFilled" className="h-5 w-5 text-indicators-yellow" />
            Driver has upcoming shifts
          </AlertDialogTitle>
          <AlertDialogDescription>
            {personName}'s sheriff type will change.{" "}
            {shiftCount > 1
              ? "These upcoming shifts don't match the new type and will be cancelled."
              : "This upcoming shift doesn't match the new type and will be cancelled."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1 py-2 text-sm">
          <div className="flex items-center gap-2 font-medium">
            <Icon name="truck" className="h-4 w-4 text-muted-foreground" />
            {shiftCount} upcoming shift{shiftCount > 1 ? "s" : ""}
          </div>
          <ul className="max-h-48 overflow-y-auto pl-6 text-muted-foreground">
            {upcomingShifts.map((shift) => (
              <li key={shift.id}>
                {formatDate(shift.start_time)}
                {shift.area ? ` · ${shift.area}` : ""}
                {shift.location ? ` · ${shift.location}` : ""}
              </li>
            ))}
          </ul>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
            }}
            disabled={isConfirming}
          >
            {isConfirming
              ? "Saving..."
              : isCompletion
                ? "Complete and cancel shifts"
                : "Change and cancel shifts"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default SheriffTypeChangeWarningDialog
